/**
* Deepblue 通用确认弹窗组件 (Confirm Dialog)
*
* 全局无侵入拦截 [data-confirm] 触发点，替代原生 window.confirm。
* 支持三种语义类型：danger（危险红）、warning（警告黄）、info（常规蓝）。
*
* 支持通过 HTML 属性扩展配置：
* - data-confirm: 确认消息正文
* - data-confirm-title: 弹窗标题（可选，默认根据类型生成）
* - data-confirm-type: 语义类型 (danger | warning | info，可选，默认智能推断)
* - data-confirm-ok: 确认按钮文案（可选，默认 "确认"）
* - data-confirm-cancel: 取消按钮文案（可选，默认 "取消"）
*/
const DIALOG_ID = "phx-global-confirm-dialog";
const ICONS = {
	danger: `<svg class="size-6 text-error" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
  </svg>`,
	warning: `<svg class="size-6 text-warning" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
  </svg>`,
	info: `<svg class="size-6 text-info" fill="none" viewBox="0 0 24 24" stroke-width="1.75" stroke="currentColor">
    <path stroke-linecap="round" stroke-linejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
  </svg>`
};
const TYPE_CONFIG = {
	danger: {
		iconBg: "bg-error/10 ring-1 ring-error/20",
		defaultTitle: "危险操作确认",
		okBtnClass: "btn btn-error text-error-content shadow-xs"
	},
	warning: {
		iconBg: "bg-warning/10 ring-1 ring-warning/20",
		defaultTitle: "操作提示",
		okBtnClass: "btn btn-warning text-warning-content shadow-xs"
	},
	info: {
		iconBg: "bg-info/10 ring-1 ring-info/20",
		defaultTitle: "提示",
		okBtnClass: "btn btn-primary text-primary-content shadow-xs"
	}
};
function inferType(message) {
	if (!message) return "danger";
	const dangerKeywords = [
		"删除",
		"彻底",
		"禁用",
		"吊销",
		"清空",
		"销毁",
		"作废",
		"注销",
		"delete",
		"destroy",
		"disable",
		"revoke"
	];
	const warningKeywords = [
		"修复",
		"重置",
		"修改",
		"覆盖",
		"更新",
		"切换",
		"reset",
		"fix",
		"update"
	];
	for (const kw of dangerKeywords) {
		if (message.includes(kw)) return "danger";
	}
	for (const kw of warningKeywords) {
		if (message.includes(kw)) return "warning";
	}
	return "danger";
}
function ensureDialogElement() {
	let dialog = document.getElementById(DIALOG_ID);
	if (dialog) return dialog;
	dialog = document.createElement("dialog");
	dialog.id = DIALOG_ID;
	dialog.className = "modal modal-bottom sm:modal-middle";
	dialog.innerHTML = `
    <div class="modal-box bg-base-100 border border-base-300 shadow-2xl p-6 sm:max-w-md w-full">
      <div class="flex items-start gap-4">
        <div id="${DIALOG_ID}-icon-box" class="shrink-0 size-11 rounded-xl flex items-center justify-center">
          <div id="${DIALOG_ID}-icon"></div>
        </div>
        <div class="flex-1 space-y-1.5 pt-0.5 min-w-0">
          <h3 id="${DIALOG_ID}-title" class="text-base font-bold text-base-content leading-6"></h3>
          <p id="${DIALOG_ID}-message" class="text-sm text-base-content/70 leading-relaxed break-words whitespace-pre-line"></p>
        </div>
      </div>
      <div class="modal-action mt-6 flex justify-end gap-3 border-t border-base-200 pt-4">
        <button id="${DIALOG_ID}-cancel-btn" type="button" class="btn btn-ghost text-base-content/80 hover:bg-base-200">
          取消
        </button>
        <button id="${DIALOG_ID}-ok-btn" type="button" class="btn">
          确认
        </button>
      </div>
    </div>
    <form method="dialog" class="modal-backdrop bg-base-content/40 backdrop-blur-xs">
      <button type="submit">close</button>
    </form>
  `;
	document.body.appendChild(dialog);
	return dialog;
}
/**
* 弹出确认弹窗，返回 Promise<boolean>
*/
export function confirmDialog(options = {}) {
	const { message = "", title = null, type = "danger", okText = "确认", cancelText = "取消" } = options;
	const resolvedType = TYPE_CONFIG[type] ? type : "danger";
	const config = TYPE_CONFIG[resolvedType];
	const resolvedTitle = title || config.defaultTitle;
	const dialog = ensureDialogElement();
	const iconBox = document.getElementById(`${DIALOG_ID}-icon-box`);
	const iconEl = document.getElementById(`${DIALOG_ID}-icon`);
	const titleEl = document.getElementById(`${DIALOG_ID}-title`);
	const messageEl = document.getElementById(`${DIALOG_ID}-message`);
	const okBtn = document.getElementById(`${DIALOG_ID}-ok-btn`);
	const cancelBtn = document.getElementById(`${DIALOG_ID}-cancel-btn`);
	// 填充内容与样式
	iconBox.className = `shrink-0 size-11 rounded-xl flex items-center justify-center ${config.iconBg}`;
	iconEl.innerHTML = ICONS[resolvedType];
	titleEl.textContent = resolvedTitle;
	messageEl.textContent = message;
	okBtn.textContent = okText;
	okBtn.className = `${config.okBtnClass} min-w-[5rem]`;
	cancelBtn.textContent = cancelText;
	return new Promise((resolve) => {
		let settled = false;
		const finish = (result) => {
			if (settled) return;
			settled = true;
			cleanup();
			if (dialog.open) {
				dialog.close();
			}
			resolve(result);
		};
		const onOk = (e) => {
			e.preventDefault();
			finish(true);
		};
		const onCancel = (e) => {
			e.preventDefault();
			finish(false);
		};
		const onDialogClose = () => {
			finish(false);
		};
		const cleanup = () => {
			okBtn.removeEventListener("click", onOk);
			cancelBtn.removeEventListener("click", onCancel);
			dialog.removeEventListener("close", onDialogClose);
		};
		okBtn.addEventListener("click", onOk);
		cancelBtn.addEventListener("click", onCancel);
		dialog.addEventListener("close", onDialogClose);
		dialog.showModal();
		// 焦点策略：危险操作默认聚焦取消按钮防误触，常规操作聚焦确认按钮
		requestAnimationFrame(() => {
			if (resolvedType === "danger") {
				cancelBtn.focus();
			} else {
				okBtn.focus();
			}
		});
	});
}
/**
* 初始化全局 data-confirm 拦截器
*/
export function initConfirmDialog() {
	if (window._phx_confirm_dialog_initialized) return;
	window._phx_confirm_dialog_initialized = true;
	// 挂载到 window 供编程式调用
	window.confirmDialog = confirmDialog;
	// 屏蔽 phoenix_html 的原生 window.confirm 事件处理，防止二次弹窗
	window.addEventListener("phoenix.link.click", (e) => {
		e.stopImmediatePropagation();
	}, true);
	// 在捕获阶段拦截 click 事件，早于 phoenix_html 和 phoenix_live_view
	document.addEventListener("click", async (event) => {
		const triggerEl = event.target && event.target.closest ? event.target.closest("[data-confirm]") : null;
		if (!triggerEl) return;
		// 如果已经被用户确认放行，清除标记并放行给下游事件监听器
		if (triggerEl._phx_confirm_approved) {
			delete triggerEl._phx_confirm_approved;
			return;
		}
		// 拦截原生事件传播与默认行为
		event.preventDefault();
		event.stopImmediatePropagation();
		const message = triggerEl.getAttribute("data-confirm") || "";
		const explicitType = triggerEl.getAttribute("data-confirm-type");
		const type = explicitType || inferType(message);
		const title = triggerEl.getAttribute("data-confirm-title");
		const okText = triggerEl.getAttribute("data-confirm-ok") || "确认";
		const cancelText = triggerEl.getAttribute("data-confirm-cancel") || "取消";
		const confirmed = await confirmDialog({
			message,
			title,
			type,
			okText,
			cancelText
		});
		if (confirmed) {
			triggerEl._phx_confirm_approved = true;
			// 触发真实点击（由于有 _phx_confirm_approved 标记，下次直接放行）
			triggerEl.click();
		}
	}, true);
}
export default initConfirmDialog;
